import { useState, useEffect, useRef, useCallback } from "react";
import { nutritionService } from "../../Services/nutrition.service";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@heroicons/react/solid";

/**
 * Nutrition Report Component
 * Displays personalized nutrition recommendations using the "Professional List" layout
 */
function Recommendations({
  petInfo,
  setPetInfo,
  setPreferredBundle,
  setStep,
  petType,
  petAllergies,
}) {
  // Allergy-filtered variety helpers — use i18n keys
  const allergyVarietyMap = {
    chicken: ['variety_chicken'],
    beef: ['variety_beef'],
    lamb: ['variety_lamb'],
    fish: ['variety_salmon'],
    turkey: ['variety_turkey'],
  };
  const allFreshVarieties = ['variety_chicken', 'variety_beef', 'variety_lamb', 'variety_salmon', 'variety_turkey'];
  const allergiesArr = petAllergies ? petAllergies.split(',').map(a => a.trim()).filter(Boolean) : [];
  const excludedVarieties = allergiesArr.flatMap(a => allergyVarietyMap[a] || []);
  const availableFreshVarieties = allFreshVarieties.filter(v => !excludedVarieties.includes(v));
  const availableDryVarieties = ['variety_lamb_dry']; // only variety in catalog

  const [nutritionData, setNutritionData] = useState(null);
  const [selectedBundleId, setSelectedBundleId] = useState(null); // Track selection by ID
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  const observerRef = useRef(null);

  // Callback ref: sets up IntersectionObserver when the calorie card mounts
  const calorieCardRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0 }
    );
    observerRef.current.observe(node);
  }, []);

  const { t } = useTranslation();

  // Mapping for badge colors and labels based on priceLevel/Bundle ID
  const getBundleStyle = (bundle) => {
    if (bundle.id === 'premium_fresh' || bundle.id === 'fresh_variety') {
      return {
        badgeColor: 'bg-orange-500',
        badgeText: t('badgePremium'),
        borderColor: 'border-orange-500',
        bgColor: 'bg-orange-50',
        hoverBorder: 'hover:border-orange-300'
      };
    } else if (bundle.id === 'balanced_mix') {
      return {
        badgeColor: 'bg-kito-green',
        badgeText: t('badgePopular'),
        borderColor: 'border-kito-green',
        bgColor: 'bg-green-50',
        hoverBorder: 'hover:border-green-300'
      };
    } else {
      return {
        badgeColor: 'bg-blue-500',
        badgeText: t('badgeEconomic'),
        borderColor: 'border-blue-500',
        bgColor: 'bg-blue-50',
        hoverBorder: 'hover:border-blue-300'
      };
    }
  };

  // Map bundle ID → i18n keys for name, description, matchReasons
  const getBundleContent = (bundleId) => {
    const map = {
      premium_fresh: {
        name: t('bundle_premium_fresh_name'),
        description: t('bundle_premium_fresh_desc'),
        matchReasons: [t('bundle_premium_fresh_reason1'), t('bundle_premium_fresh_reason2')],
      },
      balanced_mix: {
        name: t('bundle_balanced_mix_name'),
        description: t('bundle_balanced_mix_desc'),
        matchReasons: [t('bundle_balanced_mix_reason1')],
      },
      economic_dry: {
        name: t('bundle_economic_dry_name'),
        description: t('bundle_economic_dry_desc'),
        matchReasons: [t('bundle_economic_dry_reason1')],
      },
      fresh_variety: {
        name: t('bundle_fresh_variety_name'),
        description: t('bundle_fresh_variety_desc'),
        matchReasons: [t('bundle_fresh_variety_reason1'), t('bundle_fresh_variety_reason2')],
      },
    };
    return map[bundleId] || { name: bundleId, description: '', matchReasons: [] };
  };

  // Build pet profile for new nutrition API
  const buildNutritionProfile = () => {
    // Map activity level values (Frontend -> Backend Enum)
    const activityMap = {
      'lazy': 'low',
      'active': 'normal',
      'very_active': 'high'
    };

    // Map body type values (Frontend -> Backend Enum)
    const bodyTypeMap = {
      'thin': 'slim',
      'normal': 'normal',
      'fat': 'overweight'
    };

    // Map life stage values (Frontend -> Backend Enum)
    let lifeStage = petInfo.lifeStage || 'adult';
    if (lifeStage === 'senior') lifeStage = 'adult';

    return {
      petType: petType,
      weightKg: parseFloat(petInfo.kg) || 5,
      ageMonths: petInfo.ageMonths || 24,
      isNeutered: petInfo.isNeutral || false,
      activityLevel: activityMap[petInfo.activity] || "normal",
      bodyType: bodyTypeMap[petInfo.bodyType] || "normal",
      breedSize: petInfo.breedSize || "medium",
      name: petInfo.name || "",
      lifeStage: lifeStage,
      allergies: petInfo.allergies
        ? petInfo.allergies.split(',').map(a => a.trim()).filter(Boolean)
        : [],
    };
  };

  // Fetch nutrition calculation
  const fetchNutrition = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const profile = buildNutritionProfile();
      const response = await nutritionService.calculateNutrition(profile);

      setNutritionData(response.data);

      // Auto-select the "Popular" (Mixed) bundle if available, otherwise first
      const bundles = response.data.bundles || [];
      const popularBundle = bundles.find(b => b.id === 'balanced_mix');
      if (popularBundle) {
        handleBundleSelect(popularBundle);
      } else if (bundles.length > 0) {
        handleBundleSelect(bundles[0]);
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching nutrition:", err);
      setError(t("recommendationError") || "Öneri alınırken bir hata oluştu");
      setIsLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchNutrition();
  }, []);

  // Handle bundle selection
  const handleBundleSelect = (bundle) => {
    setSelectedBundleId(bundle.id);
    setPreferredBundle(bundle); // Save entire bundle object for next steps
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-kito-green border-t-transparent mb-4"></div>
        <p className="text-yellow-600 font-poppins">{t("calculatingRecommendations") || "Hesaplama yapılıyor..."}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] px-4 w-full max-w-md mx-auto">
        <div className="w-full bg-white border border-red-100 rounded-2xl p-8 flex flex-col items-center shadow-sm">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-800 font-poppins font-semibold text-base mb-1 text-center">
            {t("recommendationErrorTitle") || "Bağlantı Hatası"}
          </p>
          <p className="text-gray-400 font-poppins text-sm mb-6 text-center">
            {error}
          </p>
          <button
            onClick={fetchNutrition}
            className="w-full px-6 py-2.5 bg-kito-green text-white rounded-full font-poppins font-medium text-sm hover:bg-opacity-90 transition-all shadow-sm"
          >
            {t("tryAgain") || "Tekrar Dene"}
          </button>
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div className="flex flex-col items-center w-full max-w-2xl px-4 sm:px-6 mb-24">
      {/* Header */}
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="font-poppins text-2xl sm:text-3xl text-gray-800 font-bold mb-3 tracking-tight break-words">
          <span className="text-orange-500">{nutritionData?.petName || petInfo.name || t("yourPet")}</span> {t("calculationCompleted")}
        </h2>
        <p className="font-poppins text-gray-500 text-base max-w-md mx-auto">
          {t("nutritionReportDesc") || "Dostunuzun günlük kalori ihtiyacı ve alternatif beslenme planları aşağıda"}
        </p>
      </div>

      {/* Nutrition Summary Cards - Dynamic based on selected bundle */}
      {(() => {
        const selected = nutritionData?.bundles?.find(b => b.id === selectedBundleId);
        const freshProducts = selected?.products?.filter(p => p.productType === 'fresh') || [];
        const dryProducts = selected?.products?.filter(p => p.productType === 'dry') || [];
        const totalGrams = selected?.products?.reduce((sum, p) => sum + (p.dailyGrams || 0), 0) || 0;
        const freshGrams = freshProducts.reduce((sum, p) => sum + (p.dailyGrams || 0), 0);
        const dryGrams = dryProducts.reduce((sum, p) => sum + (p.dailyGrams || 0), 0);
        const freshMonthlyPkgs = freshProducts.reduce((sum, p) => sum + (p.monthlyPackages || 0), 0);
        const dryMonthlyPkgs = dryProducts.reduce((sum, p) => sum + (p.monthlyPackages || 0), 0);

        return (
          <div className="w-full mb-10 space-y-4">
            {/* Top row: Calorie Only (Full Width) */}
            <div className="w-full" ref={calorieCardRef}>
              {/* Calorie Card */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex flex-col items-center justify-center relative overflow-hidden group hover:shadow-md transition-all">
                <div className="absolute top-0 right-0 w-16 h-16 bg-orange-50 rounded-bl-full -mr-8 -mt-8 z-0"></div>
                <p className="text-3xl font-bold text-orange-500 z-10">{nutritionData?.dailyCalorieNeed}</p>
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mt-1 z-10">{t("kcalPerDay")}</p>
              </div>
            </div>

            {/* Bottom row: Fresh & Dry breakdown */}
            {selected && (
              <>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className={`grid ${freshGrams > 0 && dryGrams > 0 ? 'grid-cols-2 divide-x divide-gray-100' : 'grid-cols-1'}`}>
                    {/* Fresh Food Column */}
                    {freshGrams > 0 && (
                      <div className="p-4 flex flex-col items-center text-center">
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t("freshFoodGeneric")}</span>
                        </div>
                        <p className="text-xl font-bold text-gray-800">{Math.round(freshGrams)}g<span className="text-sm font-normal text-gray-400">{t("perDay")}</span></p>
                        <p className="text-xs text-gray-500 font-semibold mt-0.5">
                          ~{(freshGrams / (freshProducts[0]?.packageGr || 80)).toFixed(1)} {t("dailyPackets")}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t("monthly")} <span className="font-semibold text-gray-600">{freshMonthlyPkgs} {t("packet")}</span>
                          {freshProducts[0]?.packageGr && (
                            <span> ({freshProducts[0].packageGr >= 1000 ? (freshProducts[0].packageGr / 1000) + ' kg' : freshProducts[0].packageGr + 'g'}{t('packageSuffix')})</span>
                          )}
                        </p>
                      </div>
                    )}

                    {/* Dry Food Column */}
                    {dryGrams > 0 && (
                      <div className="p-4 flex flex-col items-center text-center">
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t("dryFoodGeneric")}</span>
                        </div>
                        <p className="text-xl font-bold text-gray-800">{Math.round(dryGrams)}g<span className="text-sm font-normal text-gray-400">{t("perDay")}</span></p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t("monthly")} <span className="font-semibold text-gray-600">~{((dryGrams * 30) / 1000).toFixed(1)} kg</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Info note: moved under the breakdown card */}
                <p className="text-center text-[10px] text-gray-400 mt-2">
                  {t('calculatedByBundle', { bundleName: getBundleContent(selected.id).name })}
                </p>
              </>
            )}
          </div>
        );
      })()}

      {/* Disclaimer / Medical Note */}
      <div className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 mb-10 flex items-start gap-3">
        <div className="mt-1 text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: t('disclaimerText') }} />
      </div>

      {/* Sticky Summary Bar — only visible when calorie card is scrolled out */}
      {showSticky && (() => {
        const sel = nutritionData?.bundles?.find(b => b.id === selectedBundleId);
        const freshProd = sel?.products?.filter(p => p.productType === 'fresh') || [];
        const dryProd = sel?.products?.filter(p => p.productType === 'dry') || [];
        const freshG = freshProd.reduce((s, p) => s + (p.dailyGrams || 0), 0);
        const dryG = dryProd.reduce((s, p) => s + (p.dailyGrams || 0), 0);
        const freshPkgs = freshProd.reduce((s, p) => s + (p.monthlyPackages || 0), 0);
        return (
          <div className="sticky top-0 z-10 w-full mb-4">
            <div className="bg-white border border-gray-300 shadow-lg rounded-2xl px-3 py-2.5 flex flex-wrap items-center gap-x-2 gap-y-1.5 overflow-x-hidden">
              {/* Kcal */}
              <div className="flex items-baseline gap-1 flex-shrink-0">
                <span className="text-base font-extrabold text-orange-500 leading-none">
                  {nutritionData?.dailyCalorieNeed}
                </span>
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{t("kcalPerDay")}</span>
              </div>

              {/* Fresh */}
              {freshG > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                  <div className="text-xs leading-tight">
                    <span className="font-bold text-gray-800">{Math.round(freshG)}g</span>
                    <span className="text-gray-400">{t("perDay")}</span>
                    {(() => {
                      const packageGr = sel?.products?.find(p => p.productType === 'fresh')?.packageGr || 80;
                      const dailyPkts = (freshG / packageGr).toFixed(1);
                      return <span className="text-gray-400 ml-1">(~{dailyPkts} {t("dailyPackets")})</span>;
                    })()}
                  </div>
                </div>
              )}

              {/* Dry */}
              {dryG > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                  <div className="text-xs leading-tight">
                    <span className="font-bold text-gray-800">{Math.round(dryG)}g</span>
                    <span className="text-gray-400">{t("perDay")}</span>
                    <span className="text-gray-400 ml-1">(~{((dryG * 30) / 1000).toFixed(1)} {t("kgPerMonth")})</span>
                  </div>
                </div>
              )}

              {/* Selected bundle name */}
              {sel && (
                <span className="text-[11px] font-semibold text-gray-500 ml-auto truncate max-w-[100px]">
                  {getBundleContent(sel.id).name}
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Bundles List */}
      <div className="w-full flex flex-col gap-6">
        {nutritionData?.bundles?.map((bundle) => {
          const style = getBundleStyle(bundle);
          const isSelected = selectedBundleId === bundle.id;

          return (
            <div
              key={bundle.id}
              onClick={() => handleBundleSelect(bundle)}
              className={`relative rounded-3xl p-4 border-2 cursor-pointer transition-all duration-300 
                ${isSelected
                  ? `${style.borderColor} ${style.bgColor} shadow-lg scale-[1.01]`
                  : `border-gray-200 bg-white hover:border-gray-300 hover:shadow-md`
                }`}
            >
              {/* Floating Badge */}
              <div className={`absolute -top-3 left-6 ${style.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full font-poppins shadow-sm`}>
                {style.badgeText}
              </div>

              {/* Selection Checkmark */}
              {isSelected && (
                <div className={`absolute -top-2 -right-2 ${style.badgeColor} text-white rounded-full p-1 shadow-md`}>
                  <CheckIcon className="h-4 w-4" />
                </div>
              )}

              <div className="mt-3 flex flex-col sm:flex-row justify-between items-start gap-2">
                <div className="flex-1">
                  {/* Title & Price */}
                  <div className="flex justify-between items-start w-full mb-1 px-2">
                    <h3 className="font-poppins font-bold text-base text-gray-800 leading-tight text-left">
                      {getBundleContent(bundle.id).name}
                    </h3>
                    <div className="text-right whitespace-nowrap">
                      <span className="font-bold text-lg text-orange-500">
                        ₺{bundle.priceEstimate.monthly}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">{t("pricePerMonth")}</span>
                    </div>
                  </div>

                  {/* Description */}
                  {getBundleContent(bundle.id).description && (() => {
                    const descColorMap = {
                      premium_fresh: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
                      fresh_variety: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
                      balanced_mix: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
                      economic_dry: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
                    };
                    const dc = descColorMap[bundle.id] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' };
                    return (
                      <p className={`text-sm ${dc.text} ${dc.bg} border ${dc.border} px-3 py-1.5 rounded-lg mb-3 text-left leading-relaxed italic`}>
                        {getBundleContent(bundle.id).description}
                      </p>
                    );
                  })()}

                  {/* Product List */}
                  <div className="bg-white/60 rounded-xl p-2 mb-2 space-y-1">
                    {bundle.products.map((product, idx) => (
                      <div key={idx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-x-2 gap-y-0.5 text-xs">
                        <div className="flex items-center text-gray-700">
                          <span className={`w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0 ${product.productType === 'fresh' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                          <span className="font-medium">
                            {product.productType === 'dry' ? t('kitoDryLabel') : product.productName}
                          </span>
                        </div>
                        <div className="text-gray-500 text-left pl-3.5 sm:pl-0 sm:text-right">
                          {product.dailyGrams}g{t("perDay")}{" "}
                          <span className="text-xs text-gray-400">
                            {product.productType === 'fresh'
                              ? `(~${(product.dailyGrams / (product.packageGr || 80)).toFixed(1)} ${t("dailyPackets")} · ${product.monthlyPackages} ${t("packetPerMonth")})`
                              : `(~${((product.dailyGrams * 30) / 1000).toFixed(1)} ${t("kgPerMonth")})`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Variety Tags */}
                  {(() => {
                    const hasFresh = bundle.products.some(p => p.productType === 'fresh');
                    const hasDry = bundle.products.some(p => p.productType === 'dry');
                    const hasVarieties = (hasFresh && availableFreshVarieties.length > 0) || hasDry;
                    return (
                      <div className="mt-2 mb-3 px-2 space-y-1">
                        {hasVarieties && (
                          <p className="text-xs font-semibold text-gray-700 mb-1 text-left">{t("alternatives")}</p>
                        )}
                        {hasFresh && availableFreshVarieties.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-[10px] text-gray-400 font-semibold mr-1">{t("freshVarietyLabel")}</span>
                            {availableFreshVarieties.map((v, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-green-50 border border-green-200 text-green-800 text-[10px] rounded-full">{t(v)}</span>
                            ))}
                          </div>
                        )}
                        {hasDry && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-[10px] text-gray-400 font-semibold mr-1">{t("dryVarietyLabel")}</span>
                            {availableDryVarieties.map((v, i) => (
                              <span key={i} className="inline-block px-2 py-0.5 bg-yellow-50 border border-yellow-200 text-yellow-800 text-[10px] rounded-full">{t(v)}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Match Reasons (Tags) */}
                  <div className="flex flex-wrap justify-start gap-1.5 px-2">
                    {bundle.matchReasons.map((key, idx) => (
                      <span key={idx} className="inline-flex items-start px-2 py-1 rounded-md text-[10px] font-medium bg-green-100 text-green-800 text-left">
                        <CheckIcon className="h-3 w-3 mr-1 text-green-600 flex-shrink-0 mt-[1px]" />
                        {t(key)}
                      </span>
                    ))}
                  </div>

                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Navigation handles the 'Next Step' button via App.js logic 
          BUT user might want a specific button here? 
          App.js handles the button for step 6.
      */}
    </div>
  );
}

export default Recommendations;
